import { Heading1, Heading2 } from 'components/shared/Typography.styled';
import { Button } from 'components/shared/Button';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  screen,
  spacing
} from 'theme';

export const StyledContainer = styled.div`
  max-width: ${container('xlMax')};
  padding: ${spacing('xl4')} ${spacing('md')} ${spacing('md')};
  margin: auto;
`;

export const StyledHero = styled.div`
  display: flex;
  align-items: flex-start;

  @media (min-width: 0) {
    flex-direction: column;
    justify-content: center;
  }

  @media (min-width: ${screen('md')}) {
    flex-direction: row;
    justify-content: space-between;
    background-color: ${color('mint_700')};
    border-radius: 16px;
    padding: 1rem 1.5rem 0.5rem 1.5rem;
  }
`;

export const StyledHeroImage = styled.img`
  order: 0;
  @media (min-width: ${screen('md')}) {
    max-width: 40%;
    min-width: 25%;
    align-self: flex-end;
  }
  @media (min-width: ${screen('xl')}) {
    max-width: 22rem;
  }
`;

export const StyledHeroTitle = styled(Heading1)`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: ${spacing('xs')};

  padding: 1rem;

  color: var(--headingColor);
  font-family: 'BigShoulders', sans-serif;

  @media (max-width: ${screen('md')}) {
    order: 1;
    font-size: ${fontSize('xl4')};

    font-family: ${fontFamily('sans')};
    font-weight: ${fontWeight('black')};
  }

  @media (min-width: ${screen('md')}) {
    &::before {
      content: '';
      width: 80px;
      height: 8px;
      flex-shrink: 0;
      background-color: var(--headingColor);
      margin-bottom: 1rem;
    }

    order: 0;
    text-transform: uppercase;
    font-size: ${fontSize('xl6')};
    margin-bottom: ${spacing('lg')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl10')};
  }

  .pretitle {
    margin: 0;
    text-transform: uppercase;

    @media (min-width: 0) {
      font-size: ${fontSize('md')};
      line-height: 24px;
      font-weight: 900;
      letter-spacing: 5.6px;
    }

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('lg')};
      letter-spacing: unset;
      line-height: 1rem;
      font-weight: 700;
    }
    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl')};
    }

    @media (min-width: ${screen('xl')}) {
      font-size: ${fontSize('xl2')};
      font-weight: ${fontWeight('bold')};
      letter-spacing: unset;
    }
  }
`;

export const StyledIntro = styled.div`
  max-width: ${container('md')};
  margin: auto;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};

  @media (min-width: ${screen('md')}) {
    margin-top: ${spacing('xl4')};
  }
`;

export const StyledIntroText = styled.div`
  margin-bottom: ${spacing('lg')};
  line-height: 1.5;

  &:not(.display-full-text) {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    @media (min-width: ${screen('md')}) {
      -webkit-line-clamp: 11;
    }
  }

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }
`;

export const StyledReadMoreButton = styled(Button)`
  @media (min-width: ${screen('md')}) {
    display: none;
  }
`;

export const StyledIntroTitle = styled(Heading2)`
  margin-bottom: ${spacing('md')};

  &::before {
    content: '🏃‍♂️💨';
    display: block;
  }
`;

export const StyledData = styled.div`
  max-width: ${container('xlMax')};
  margin: ${spacing('xl4')} auto;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};
`;

export const StyledDataTitle = styled.h4`
  font-family: ${fontFamily('sans')};
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.35em;
  margin-top: 0;
  font-weight: ${fontWeight('black')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing('xs')};
  text-align: center;

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('lg')};
  }

  &::before,
  &::after {
    content: '';
    height: 1px;
    background-color: ${color('midnight_900')};
    flex-grow: 1;
  }
`;

export const StyledDataList = styled.div`
  max-width: ${container('lg')};
  margin: auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl2')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${screen('lg')}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledDataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  font-weight: ${fontWeight('black')};
  font-size: ${fontSize('md')};
  line-height: 1.25;
  flex-direction: column;
  gap: ${spacing('xs')};
`;

export const StyledDataValue = styled.span`
  color: ${color('navy_500')};
  font-family: ${fontFamily('display')};
  font-weight: ${fontWeight('semiBold')};
  font-size: ${fontSize('xl6')};
  line-height: 1;
  flex-shrink: 0;
  width: auto;
  text-align: center;
`;

export const StyledDataContent = styled.span`
  display: flex;
  gap: ${spacing('xs')};
  flex-direction: column;
  text-align: center;

  &::before {
    content: '';
    display: block;
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: ${color('navy_200')};
    margin-left: auto;
    margin-right: auto;
  }
`;
